/**
 * Permission Utility Functions
 * Centralized permission checking logic for frontend
 */

export interface User {
  _id?: string;
  id?: string;
  email?: string;
  fullName?: string;
  isSuperAdmin?: boolean;
  permissionCodes?: string[];
  tenantId?: string;
  tenantIds?: string[];
}

/**
 * Check if user has a specific permission
 * @param user - Current user object
 * @param permissionCode - Permission code to check (e.g., 'CANDIDATES_READ')
 * @returns true if user has permission, false otherwise
 */
export function hasPermission(
  user: User | null | undefined,
  permissionCode: string
): boolean {
  if (!user) return false;

  // Super admins have all permissions
  if (user.isSuperAdmin) return true;

  // Check if user has the specific permission
  return user.permissionCodes?.includes(permissionCode) ?? false;
}

/**
 * Check if user has ANY of the specified permissions (OR logic)
 * @param user - Current user object
 * @param permissionCodes - Array of permission codes
 * @returns true if user has at least one permission
 */
export function hasAnyPermission(
  user: User | null | undefined,
  permissionCodes: string[]
): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  return permissionCodes.some((code) => user.permissionCodes?.includes(code));
}

/**
 * Check if user has ALL of the specified permissions (AND logic)
 * @param user - Current user object
 * @param permissionCodes - Array of permission codes
 * @returns true if user has all permissions
 */
export function hasAllPermissions(
  user: User | null | undefined,
  permissionCodes: string[]
): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  return permissionCodes.every((code) => user.permissionCodes?.includes(code));
}

/**
 * Check if user is a super admin
 * @param user - Current user object
 * @returns true if user is super admin
 */
export function isSuperAdmin(user: User | null | undefined): boolean {
  if (!user) {
    console.log("🔍 [isSuperAdmin] No user object provided");
    return false;
  }
  
  // Handle both boolean and string values for isSuperAdmin (API might return either)
  const isSuper = user.isSuperAdmin === true || (user.isSuperAdmin as unknown) === "true";
  
  console.log("🔍 [isSuperAdmin] Check result:", {
    userEmail: user.email,
    isSuperAdmin: user.isSuperAdmin,
    type: typeof user.isSuperAdmin,
    result: isSuper,
  });
  
  return isSuper;
}

/**
 * Get all permissions for a user
 * @param user - Current user object
 * @returns Array of permission codes
 */
export function getUserPermissions(user: User | null | undefined): string[] {
  if (!user) return [];
  if (user.isSuperAdmin) return ["*"];
  return user.permissionCodes || [];
}

/**
 * Permission codes enum for type safety
 */
export const PermissionCodes = {
  // Dashboard
  DASHBOARD_READ: "DASHBOARD_READ",

  // Tenants
  TENANTS_READ: "TENANTS_READ",
  TENANTS_CREATE: "TENANTS_CREATE",
  TENANTS_UPDATE: "TENANTS_UPDATE",
  TENANTS_DELETE: "TENANTS_DELETE",

  // Tenant Settings
  TENANT_SETTINGS_READ: "TENANT_SETTINGS_READ",
  TENANT_SETTINGS_UPDATE: "TENANT_SETTINGS_UPDATE",

  // Tenant Users
  TENANT_USERS_READ: "TENANT_USERS_READ",
  TENANT_USERS_CREATE: "TENANT_USERS_CREATE",
  TENANT_USERS_UPDATE: "TENANT_USERS_UPDATE",
  TENANT_USERS_DELETE: "TENANT_USERS_DELETE",

  // Tenant Roles
  TENANT_ROLES_READ: "TENANT_ROLES_READ",
  TENANT_ROLES_CREATE: "TENANT_ROLES_CREATE",
  TENANT_ROLES_UPDATE: "TENANT_ROLES_UPDATE",
  TENANT_ROLES_DELETE: "TENANT_ROLES_DELETE",

  // Requisitions
  REQUISITIONS_READ: "REQUISITIONS_READ",
  REQUISITIONS_CREATE: "REQUISITIONS_CREATE",
  REQUISITIONS_UPDATE: "REQUISITIONS_UPDATE",
  REQUISITIONS_DELETE: "REQUISITIONS_DELETE",

  // Candidates
  CANDIDATES_READ: "CANDIDATES_READ",
  CANDIDATES_CREATE: "CANDIDATES_CREATE",
  CANDIDATES_UPDATE: "CANDIDATES_UPDATE",
  CANDIDATES_DELETE: "CANDIDATES_DELETE",

  // Submissions
  SUBMISSIONS_READ: "SUBMISSIONS_READ",
  SUBMISSIONS_CREATE: "SUBMISSIONS_CREATE",
  SUBMISSIONS_UPDATE: "SUBMISSIONS_UPDATE",
  SUBMISSIONS_DELETE: "SUBMISSIONS_DELETE",

  // Vendors
  VENDORS_READ: "VENDORS_READ",
  VENDORS_CREATE: "VENDORS_CREATE",
  VENDORS_UPDATE: "VENDORS_UPDATE",
  VENDORS_DELETE: "VENDORS_DELETE",

  // Analytics
  ANALYTICS_READ: "ANALYTICS_READ",

  // Reports
  REPORTS_READ: "REPORTS_READ",
  REPORTS_EXPORT: "REPORTS_EXPORT",

  // Settings
  SETTINGS_READ: "SETTINGS_READ",
  SETTINGS_UPDATE: "SETTINGS_UPDATE",

  // AI Insights
  AI_INSIGHTS_READ_RESULTS: "AI_INSIGHTS_READ_RESULTS",
  AI_TASKS_CREATE: "AI_TASKS_CREATE",
  AI_TASKS_EXECUTE: "AI_TASKS_EXECUTE",
  AI_TASKS_APPROVE: "AI_TASKS_APPROVE",

  // Data Management
  DATA_MANAGEMENT_READ: "DATA_MANAGEMENT_READ",
  DATA_MANAGEMENT_IMPORT_CANDIDATES: "DATA_MANAGEMENT_IMPORT_CANDIDATES",
  DATA_MANAGEMENT_APPROVE_IMPORTS: "DATA_MANAGEMENT_APPROVE_IMPORTS",
  DATA_MANAGEMENT_VIEW_HISTORY: "DATA_MANAGEMENT_VIEW_HISTORY",
} as const;

/**
 * Permission groups for easier management
 */
export const PermissionGroups = {
  // Module read access
  canAccessDashboard: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.DASHBOARD_READ),
  canAccessTenants: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANTS_READ) || isSuperAdmin(user),
  canAccessRequisitions: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.REQUISITIONS_READ),
  canAccessCandidates: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.CANDIDATES_READ),
  canAccessSubmissions: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.SUBMISSIONS_READ),
  canAccessVendors: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.VENDORS_READ),
  canAccessAnalytics: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.ANALYTICS_READ),
  canAccessReports: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.REPORTS_READ),
  canAccessSettings: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.SETTINGS_READ),
  canAccessTenantSettings: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_SETTINGS_READ),
  canAccessTenantUsers: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_USERS_READ),
  canAccessTenantRoles: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_ROLES_READ),
  canAccessDataManagement: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.DATA_MANAGEMENT_READ),
  canAccessAIInsights: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.AI_INSIGHTS_READ_RESULTS),

  // CRUD operations
  canCreateRequisition: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.REQUISITIONS_CREATE),
  canUpdateRequisition: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.REQUISITIONS_UPDATE),
  canDeleteRequisition: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.REQUISITIONS_DELETE),

  canCreateCandidate: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.CANDIDATES_CREATE),
  canUpdateCandidate: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.CANDIDATES_UPDATE),
  canDeleteCandidate: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.CANDIDATES_DELETE),

  canCreateSubmission: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.SUBMISSIONS_CREATE),
  canUpdateSubmission: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.SUBMISSIONS_UPDATE),
  canDeleteSubmission: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.SUBMISSIONS_DELETE),

  // Admin functions
  canManageTenantUsers: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_USERS_READ),
  canManageTenantRoles: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_ROLES_READ),
  canManageTenantSettings: (user: User | null | undefined) =>
    hasPermission(user, PermissionCodes.TENANT_SETTINGS_READ),
};

/**
 * Route permission mapping
 * Maps route paths to required permissions
 */
export const RoutePermissions: Record<string, string> = {
  "/dashboard": PermissionCodes.DASHBOARD_READ,
  "/tenants": PermissionCodes.TENANTS_READ,
  "/tenants/new": PermissionCodes.TENANTS_CREATE,
  "/requisitions": PermissionCodes.REQUISITIONS_READ,
  "/requisitions/new": PermissionCodes.REQUISITIONS_CREATE,
  "/candidates": PermissionCodes.CANDIDATES_READ,
  "/candidates/new": PermissionCodes.CANDIDATES_CREATE,
  "/submissions": PermissionCodes.SUBMISSIONS_READ,
  "/submissions/new": PermissionCodes.SUBMISSIONS_CREATE,
  "/vendors": PermissionCodes.VENDORS_READ,
  "/vendors/new": PermissionCodes.VENDORS_CREATE,
  "/analytics": PermissionCodes.ANALYTICS_READ,
  "/settings": PermissionCodes.SETTINGS_READ,
  "/settings/tenant": PermissionCodes.TENANT_SETTINGS_READ,
  "/settings/users": PermissionCodes.TENANT_USERS_READ,
  "/settings/roles": PermissionCodes.TENANT_ROLES_READ,
  "/data-management": PermissionCodes.DATA_MANAGEMENT_READ,
};

/**
 * Get required permission for a route
 * @param path - Route path
 * @returns Permission code required for the route, or null if no permission required
 */
export function getRoutePermission(path: string): string | null {
  // Check exact match first
  if (RoutePermissions[path]) {
    return RoutePermissions[path];
  }

  // Check for partial matches (e.g., /candidates/:id matches /candidates)
  // Sort keys by length (descending) to match more specific routes first
  const keys = Object.keys(RoutePermissions).sort((a, b) => b.length - a.length);
  
  for (const key of keys) {
    // Handle dynamic route segments (e.g., /tenants/123 matches /tenants)
    if (path.startsWith(key + "/") || path === key) {
      console.log(`🔍 [getRoutePermission] Matched route ${path} with pattern ${key}, permission: ${RoutePermissions[key]}`);
      return RoutePermissions[key];
    }
  }

  console.log(`⚠️ [getRoutePermission] No permission mapping found for path: ${path}`);
  return null;
}

/**
 * Check if user can access a specific route
 * @param user - Current user object
 * @param path - Route path
 * @returns true if user can access the route
 */
export function canAccessRoute(
  user: User | null | undefined,
  path: string
): boolean {
  console.log("🔍 [canAccessRoute] Starting access check:", {
    path,
    userEmail: user?.email,
    userIsSuperAdmin: user?.isSuperAdmin,
    userIsSuperAdminType: typeof user?.isSuperAdmin,
  });

  // Super admins can access all routes
  if (isSuperAdmin(user)) {
    console.log("✅ [canAccessRoute] Super Admin access granted for:", path);
    return true;
  }

  const requiredPermission = getRoutePermission(path);

  console.log("🔍 [canAccessRoute] Permission check details:", {
    path,
    requiredPermission,
    userEmail: user?.email,
    userIsSuperAdmin: user?.isSuperAdmin,
    userPermissions: user?.permissionCodes?.slice(0, 5) || [],
    totalPermissions: user?.permissionCodes?.length || 0,
  });

  // If no permission required, allow access
  if (!requiredPermission) {
    console.log("✅ [canAccessRoute] No permission required for:", path);
    return true;
  }

  // Check if user has the required permission
  const hasAccess = hasPermission(user, requiredPermission);
  
  console.log("🔍 [canAccessRoute] Final result:", {
    path,
    requiredPermission,
    hasAccess,
    userHasPermission: user?.permissionCodes?.includes(requiredPermission),
  });
  
  return hasAccess;
}
